import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import bannerFromTheAboutPage from '@/assets/images/about/banner-from-the-about-us-page.webp'
import logoLavid from '@/assets/images/logo-lavid.webp'
import logoUFPB from '@/assets/images/logo-ufpb.webp'
import { ScrollArea, ScrollBar } from '@/components/scroll-area'
import { Skeleton } from '@/components/skeleton'
import { Team } from '@/models/team'
import api from '@/services/api'

import { OurTeam } from './components/our-team'

export function About() {
  const [members, setMembers] = useState<Team[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  useEffect(() => {
    async function getTeamMembers() {
      await api
        .get<Team[]>('/team/members')
        .then((response) => setMembers(response.data))
        .catch((error) => toast.error(error.response.data.message))
        .finally(() => setIsLoading(false))
    }
    getTeamMembers()
  }, [])

  return (
    <>
      <div className="content-container">
        <img
          className="h-52 w-full rounded-2xl object-cover xl:h-104 2xl:h-banner"
          src={bannerFromTheAboutPage}
          alt="Banner from the about us page"
          loading="lazy"
        />

        <main className="mt-10 space-y-16 xl:mt-16 2xl:mt-20 2xl:space-y-20">
          {isLoading ? (
            <ScrollArea className="md:overflow-x-hidden">
              <div className="w-[300vw] md:w-auto">
                <div className="flex gap-8 md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  <Skeleton className="h-64 w-full" quantity={6} />
                </div>
              </div>
              <ScrollBar orientation="horizontal" className="md:hidden" />
            </ScrollArea>
          ) : (
            members && members.length > 0 && <OurTeam members={members} />
          )}

          <div className="title-with-paragraph">
            <h1 className="title">A ideia original</h1>

            <div className="space-y-6">
              <p className="paragraph">
                O projeto da 6BIOS nasceu em 2021, a partir da dissertação de
                mestrado de um dos seus fundadores - Marcelo Borges - na PUC Rio
                sob a orientação do Prof. Dr. Paulo Esteves e sua equipe. O
                projeto tratou em linhas gerais do conceito de Floresta 4.0. No
                trabalho já estava descrito, de forma seminal, a construção de
                um fundo de preservação incluindo o uso pioneiro de blockchain
                aplicada à preservação ambiental.
              </p>
              <p className="paragraph">
                Em parceria com a equipe de pesquisadores do LAVID da UFPB, sob
                a coordenação do Prof. Dr. Guido Lemos, foi desenvolvido o
                protótipo do conceito central da 6Bios, posteriormente publicado
                como whitepaper disponível no endereço eletrônico
                www.standingforest.org e no site arXiv da Cornell University.
              </p>
            </div>
          </div>

          <div className="title-with-paragraph rounded-xl bg-white py-8 xl:py-16 2xl:py-20">
            <h1 className="title">A participação do LAVID e da UFPB</h1>

            <p className="paragraph">
              O LAVID Laboratório de Vídeo Digital da Universidade Federal da
              Paraíba, foi responsável por desenvolver e expandir a ideia
              original nascida na PUC Rio. Foi criado em 2003 pelo Prof. Guido
              Lemos, co-fundador da 6BIOS.
            </p>

            <h2 className="subtitle">
              Sua história gloriosa vem ocorrendo dentro de uma importante
              universidade pública, a UFPB - Universidade Federal da Paraíba.
              Entre muitas conquistas destacam-se:
            </h2>

            <ul className="list-inside list-disc space-y-6 text-start marker:mr-0 marker:text-bios-primary">
              <li className="paragraph">
                Possui 25 cientistas pesquisadores e mais de 210 alunos das
                áreas da ciência da computação, engenharia de computação,
                comunicação, pedagogia e antropologia.
              </li>
              <li className="paragraph">
                É referência mundial em inovação e tecnologia de vídeo digital.
              </li>
              <li className="paragraph">
                Criador do firmware da TV digital com 150 milhões de cópias e
                mais de 40 milhões de licenças exportadas para a China.
              </li>
              <li className="paragraph">
                Desenvolvedor de diplomas digitais, serviço oferecido pela RNP
                com adesão de 170 instituições federais de ensino superior.
              </li>
              <li className="paragraph">
                ViLibras é referenciado por mais de 800 mil páginas, está em
                todas as páginas do governo federal, senado, câmara e diversas
                empresas e é a base utilizada hoje pela Dolby Laboratories em
                cinemas ao redor do mundo (Dolby Accessibility Solutions for
                Cinema), através de uma das suas startups - a Assista.
              </li>
              <li className="paragraph">
                A UFPB é líder brasileira no depósito de patentes.
              </li>
            </ul>

            <h3 className="subtitle text-lg xl:text-2xl">
              Recentemente, seguindo tendências das principais universidades do
              mundo, o LAVID passou a gerar startups de sucesso. Estas já
              desenvolvem vários projetos e negócios no Brasil, Estados Unidos e
              Europa.
            </h3>

            <div className="mt-10! flex flex-col items-center justify-center gap-10 md:flex-row xl:mt-20! xl:gap-20">
              <img
                className="h-40 object-cover xl:h-60"
                src={logoUFPB}
                alt="Logo da UFPB"
              />
              <img
                className="h-20 object-cover xl:h-28"
                src={logoLavid}
                alt="Logo da Lavid"
              />
            </div>
          </div>

          <div className="title-with-paragraph">
            <h1 className="title">Nosso propósito: a vida na Terra</h1>
            <div className="space-y-6">
              <p className="paragraph">
                Originada nas mais conceituadas universidades do Brasil, a 6Bios
                incorporou novos modelos de negócio, colaboração de cientistas
                de várias partes do mundo, juristas, professores e consultores
                da UFPE, UNIFESP, inteligência financeira no padrão das melhores
                FINTECHs do mercado e uma gestão internacional experiente.
              </p>
              <p className="paragraph">
                Atualmente opera como uma empresa criada para gerar formas
                práticas de fomento que permitam às pessoas, em todo o mundo,
                custearem projetos mediante doações estruturadas que promovam a
                conservação dos principais biomas brasileiros, fundamentais para
                o equilíbrio climático e ecológico do Planeta.
              </p>
              <p className="paragraph">
                A 6Bios utiliza tecnologias avançadas que viabilizam em âmbito
                global a remuneração de atividades de preservação, regeneração e
                amortecimento ambiental no Brasil e, nos casos de modelos
                econômicos de exploração autossustentável, a manutenção dos
                habitantes locais em condições dignas de trabalho e sustento,
                trazendo assim bases sólidas para a preservação contínua de cada
                bioma.
              </p>
            </div>
          </div>
        </main>
      </div>

      {/* TODO: A seção do vídeo institucional foi comentada a pedido do cliente, ver futuramente para remover comentário
      <div className="space-y-6 bg-white px-5 py-10 md:px-10 lg:px-24 xl:space-y-10 xl:px-52 2xl:px-96 2xl:py-20">
        <h1 className="title">
          Assista nosso vídeo institucional e conheça nossos propósitos
        </h1>
        <iframe
          className="aspect-video w-full"
          src="https://www.youtube.com/embed/QW42aK1UDFQ?si=2zJ2damPITi9NmbA"
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div> */}
    </>
  )
}
